<template>
	<CDialog
		:visible="show"
		title="Allow guests to edit their profile or register?"
		custom-class="dialog--6"
		data-testid="guest-list-enabled-dialog"
		@close="onClose"
		@closed="$emit('closed')"
	>
		<div class="guest-list-enabled-dialog">
			<CCheckbox :value="canEditProfile" :helptext="`Allow guests to edit their entry on ${formName}`" class="mb-4" @change="canEditProfile = $event">
				<div class="flex items-center gap-3">
					<Inline svg="edit-profile" class="text-icon-subtle" />
					<span>Allow guests to edit their profile</span>
				</div>
			</CCheckbox>
			<CCheckbox :value="canRegister" class="mb-4" :helptext="`New guests will have the option to register on ${formName}. Existing guests will enter directly into your organization.`" @change="canRegister = $event">
				<div class="flex items-center gap-3">
					<Inline svg="register" class="text-icon-subtle" />
					<span>Enable registration for new guests</span>
				</div>
			</CCheckbox>
		</div>
		<template #footer>
			<CButton class="button--secondary" @click="onClose">Cancel</CButton>
			<CButton class="button--primary" @click="onSave">Save</CButton>
		</template>
	</CDialog>
</template>

<script lang="ts">
	import { defineComponent, ref, watch, type PropType } from 'vue';
	import CDialog from 'src/components/dialogs/Dialog.vue';
	import CButton from 'src/components/interaction/Button.vue';
	import CCheckbox from 'src/components/form-controls/Checkbox.vue';
	import type { IGuestAccessList } from 'src/views/settings/organization/GuestAccessList.vue';
	import type { UpdateGuestListEvent } from 'src/views/settings/organization/OrganizationGuestAccess.vue';

	export default defineComponent({
		name: 'CGuestListEnabledDialog',
		components: {
			CDialog,
			CButton,
			CCheckbox
		},
		props: {
			show: {
				type: Boolean,
				required: true
			},
			guestList: {
				type: Object as PropType<IGuestAccessList>,
				default: null
			}
		},
		emits: ['update', 'close', 'closed'],
		setup(props, { emit }) {
			const formName = ref('this form');
			const canEditProfile = ref(false);
			const canRegister = ref(false);
			const isUpdated = ref(false);

			watch(() => props.show, (isOpen) => {
				if (!isOpen)
					return;

				formName.value = props.guestList.Name;
				canEditProfile.value = props.guestList.CanEditProfile;
				canRegister.value = props.guestList.CanRegister;
				isUpdated.value = false;
			});

			const onSave = () => {
				isUpdated.value = true;
				emit('update', {
					formId: props.guestList.Id,
					enabled: true,
					canEditProfile: canEditProfile.value,
					canRegister: canRegister.value
				} as UpdateGuestListEvent);

				emit('close');
			};

			const onClose = () => {
				if (!isUpdated.value) {
					isUpdated.value = true;
					emit('update', {
						formId: props.guestList.Id,
						enabled: true,
						canEditProfile: undefined,
						canRegister: undefined
					} as UpdateGuestListEvent);
				}

				emit('close');
			};

			return {
				onSave,
				onClose,
				formName,
				canEditProfile,
				canRegister
			};
		}
	});
</script>

<style lang="scss">
.guest-list-enabled-dialog {

	.checkbox__slot {
		margin-top: 0;
	}
}
</style>